import { Helmet } from 'react-helmet-async';
import { ArrowLeft } from 'lucide-react';
import { Link } from 'react-router-dom';

export default function Privacy() {
  return (
    <>
      <Helmet>
        <title>Privacy Policy — AURA</title>
        <meta name="description" content="AURA Privacy Policy - How we collect, use, and protect your personal information." />
      </Helmet>
      
      <div className="min-h-screen bg-background text-foreground">
        <div className="max-w-4xl mx-auto px-6 py-16">
          <Link 
            to="/" 
            className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors mb-8"
          >
            <ArrowLeft size={16} />
            Back to AURA
          </Link>
          
          <h1 className="text-4xl md:text-5xl font-serif mb-4">Privacy Policy</h1>
          <p className="text-sm text-muted-foreground mb-12">Last updated: January 2025</p>
          
          <div className="prose prose-neutral dark:prose-invert max-w-none space-y-8">
            <section>
              <h2 className="text-2xl font-serif mb-4">1. Introduction</h2>
              <p className="text-muted-foreground leading-relaxed">
                AURA ("we," "our," or "us") is committed to protecting your privacy. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our fashion intelligence platform and mobile application.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-serif mb-4">2. Information We Collect</h2>
              <h3 className="text-lg font-medium mb-2">Personal Information</h3>
              <ul className="list-disc pl-6 text-muted-foreground space-y-2">
                <li>Email address and display name during account registration</li>
                <li>Profile photos and avatar images you upload</li>
                <li>Style preferences and fashion goals from style quizzes</li>
                <li>Location data (with your permission) for weather-based recommendations</li>
              </ul>
              
              <h3 className="text-lg font-medium mb-2 mt-4">Wardrobe Data</h3>
              <ul className="list-disc pl-6 text-muted-foreground space-y-2">
                <li>Photos of clothing items you upload to your digital archive</li>
                <li>AI-analyzed attributes (color, type, brand, occasion)</li>
                <li>Outfit combinations and calendar data</li>
                <li>Packing lists and trip information</li>
              </ul>
              
              <h3 className="text-lg font-medium mb-2 mt-4">Usage Data</h3>
              <ul className="list-disc pl-6 text-muted-foreground space-y-2">
                <li>App interactions and feature usage patterns</li>
                <li>Device information and browser type</li>
                <li>IP address and approximate location</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-serif mb-4">3. How We Use Your Information</h2>
              <ul className="list-disc pl-6 text-muted-foreground space-y-2">
                <li>To provide personalized AI-powered outfit recommendations</li>
                <li>To analyze and categorize your wardrobe items using computer vision</li>
                <li>To deliver weather-appropriate style suggestions</li>
                <li>To enable social features like sharing looks and following style icons</li>
                <li>To process premium subscription payments via Razorpay</li>
                <li>To send push notifications (with your consent) for outfit reminders</li>
                <li>To improve our AI algorithms and user experience</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-serif mb-4">4. Third-Party Services</h2>
              <p className="text-muted-foreground leading-relaxed mb-4">
                We integrate with the following third-party services:
              </p>
              <ul className="list-disc pl-6 text-muted-foreground space-y-2">
                <li><strong>Google Gemini AI:</strong> For clothing analysis and outfit generation</li>
                <li><strong>Open-Meteo:</strong> For weather data to inform recommendations</li>
                <li><strong>Razorpay:</strong> For secure payment processing (India)</li>
                <li><strong>Supabase:</strong> For secure data storage and authentication</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-serif mb-4">5. Data Security</h2>
              <p className="text-muted-foreground leading-relaxed">
                We implement industry-standard security measures including encryption at rest and in transit, row-level security policies, and regular security audits. Your wardrobe images are stored securely and are only accessible to you unless you choose to share them publicly.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-serif mb-4">6. Your Rights</h2>
              <p className="text-muted-foreground leading-relaxed mb-4">
                Under GDPR and applicable Indian data protection laws, you have the right to:
              </p>
              <ul className="list-disc pl-6 text-muted-foreground space-y-2">
                <li>Access your personal data</li>
                <li>Request correction of inaccurate data</li>
                <li>Request deletion of your data</li>
                <li>Export your wardrobe data</li>
                <li>Withdraw consent for marketing communications</li>
                <li>Object to automated decision-making</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-serif mb-4">7. Data Retention</h2>
              <p className="text-muted-foreground leading-relaxed">
                We retain your data for as long as your account is active. Upon account deletion, your personal data and wardrobe images are permanently deleted within 30 days, except where we are required to retain data for legal compliance.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-serif mb-4">8. Children's Privacy</h2>
              <p className="text-muted-foreground leading-relaxed">
                AURA is not intended for users under 13 years of age. We do not knowingly collect personal information from children under 13.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-serif mb-4">9. Changes to This Policy</h2>
              <p className="text-muted-foreground leading-relaxed">
                We may update this Privacy Policy from time to time. We will notify you of any changes by posting the new policy on this page and updating the "Last updated" date.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-serif mb-4">10. Contact Us</h2>
              <p className="text-muted-foreground leading-relaxed">
                If you have questions about this Privacy Policy or wish to exercise your data rights, please contact us at:
              </p>
              <p className="text-muted-foreground mt-2">
                <strong>Email:</strong> privacy@aura.style<br />
                <strong>Address:</strong> BWE Studio, Mumbai, India
              </p>
            </section>
          </div>
          
          <div className="mt-16 pt-8 border-t border-border text-center">
            <p className="text-xs text-muted-foreground">
              Built by{' '}
              <a 
                href="https://bwestudios.com" 
                target="_blank" 
                rel="noopener noreferrer" 
                className="font-bold hover:text-foreground transition-colors"
              >
                BWE Studio
              </a>
            </p>
          </div>
        </div>
      </div>
    </>
  );
}
